import { Grid, useEventCallback } from '@mui/material';
import { memo, ReactElement, useMemo } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { noop } from 'shell/utils/noop';
import { OperationObject } from 'system/utils/event-batch/filters-object-to-query-string-batch-event';
import { ReportListVM } from 'civil-status/view-models/research-report';
import { getValueList } from 'civil-status/constants/states-act';
import { CsvExtraction } from 'shell/components/shared/csv-extraction';
import { ResponseCsv } from 'shell/models/response-csv';
import { HttpResponse } from 'shell/models/http-response';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { base64ToBlob } from 'shell/utils/base64-to-blob';
import { downloadBlob } from 'front-office/utils/download-blob';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { genericTransformer } from 'shell/utils/generic-transformer';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { DateTime } from 'luxon';

interface TableReportProps {
  data?: Array<ReportListVM>;
  filterSelected?: OperationObject;
  getReportListData: (data: OperationObject, page?: number, rowsPerPage?: number) => void;
  tableProps?: Record<string, number>;
  setTableProps: (values: Record<string, number>) => void;
}

const TableReportComponent = ({
  data,
  filterSelected,
  getReportListData,
  tableProps = {},
  setTableProps,
}: TableReportProps): ReactElement => {
  const body: Array<ReportListVM> = useMemo(() => {
    return (data || []).map((item) => ({
      ...item,
      state: getValueList(item.state),
    }));
  }, [data]);

  const onChangePage = useEventCallback((page: number) => {
    getReportListData(filterSelected || {}, page, tableProps.rowsPerPage);

    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    getReportListData(filterSelected || {}, 0, rowsPerPage);

    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  const getReportCSV = useHttpClient<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>(
    `${__ACT_API_URL__}/report/estrazione-csv`,
    {
      transformer: genericTransformer,
    }
  );

  const onClickPrintCSV = useEventCallback(() => {
    const queryParams = `${paginationListToQueryString({
      filters: filterSelected || {},
      orders: {},
    })}`;

    (
      getReportCSV({
        params: {},
        paramsSerializer: () => queryParams,
      }) as ResponsePromise<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>
    ).then((response) => {
      const responseCasted = response as HttpResponse<ResponseCsv>;
      const csv = responseCasted?.payload?.contenutoCsv;
      if (csv) {
        // download csv
        const blob = base64ToBlob(csv);
        downloadBlob(blob, `export-csv-${DateTime.now().toFormat('ddMMyyHHmmss')}.csv`);
      }
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <CsvExtraction onClick={onClickPrintCSV} title="Lista_Report" />
      </Grid>
      <Grid item xs={12}>
        <Table
          ariaLabel="Lista report"
          page={tableProps.page || 0}
          rowsPerPage={tableProps.rowsPerPage || 10}
          body={body}
          count={(body && body[0]?.totalCount) || 0}
          head={headCells}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          orders={{}}
          isNotLayoutFixed
          noSort
          hideButtons
          isServer
        />
      </Grid>
    </Grid>
  );
};

TableReportComponent.displayName = 'Municipality List - Table component';

const TableReport = memo(TableReportComponent);

export default TableReport;
